<script setup lang="ts">
const appConfig = useAppConfig();
const runtimeConfig = useRuntimeConfig()

useSeoMeta({
	title: `Terms of Service | ${appConfig.title} - Our Guidelines and Policies`,
	ogTitle: `Terms of Service | ${appConfig.title} - Our Guidelines and Policies`,
	description: `Review the Terms of Service for Kichieats. Understand the guidelines and policies governing your use of our platform, including user responsibilities, rights, and obligations. `,
	ogDescription: `Review the Terms of Service for Kichieats. Understand the guidelines and policies governing your use of our platform, including user responsibilities, rights, and obligations. `,
	ogImage: appConfig.defaultSeoImg,
	twitterCard: 'summary_large_image',
	twitterImage: `https://img.kichieats.ph/defaultSeoImg.png`,
});

definePageMeta({
	layout: 'dashboard',
});
</script>

<template>
	<Block class="mx-auto max-w-[116rem] px-[2rem]">
		<Section class="flex mt-[2.4rem] pb-[8rem] gap-[4rem] items-start w-full">
			<Section class="w-full">
				<TitleText
					class="text-center"
					type="2"
				>
					Terms of Service
				</TitleText>

				<Block class="mt-[2.4rem]">
					<!-- <ContactUsForm /> -->
					<Block class="mt-[2.4rem] border-[lightGray] border-[.1rem] py-[2.4rem] flex flex-col justify-center items-center rounded-[.8rem] max-md:p-[1.6rem]">
						<Block
							class="max-w-[84rem] w-full mx-auto"
						>
							<Block class="mt-[2.4rem] mb-[2.4rem] max-md:mt-0">
								<p class="text-[1.4rem] leading-[1.5]">
									Terms of Service for KICHIEATS
								</p>

								<p class="mt-[2.4rem] text-[1.4rem] leading-[1.5] italic">
									Last Updated: 02/04/2025
								</p>

								<p class="mt-[2.4rem] text-[1.4rem] leading-[1.5]">
									Welcome to KICHIEATS ("we," "us," or "our"). These Terms of Service ("Terms") govern your access to and use of our website and services (collectively, the "Service"). By using our Service, you agree to be bound by these Terms. If you do not agree, please do not use our Service.
								</p>

								<TitleText
									class="mt-[2.4rem]"
									type="6"
								>
									1. Acceptance of Terms
								</TitleText>

								<p class="mt-[1.6rem] text-[1.4rem] leading-[1.5]">
									By accessing or using the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms, including our Privacy Policy and Cookie Policy, which are incorporated by reference. If you do not agree to these Terms, you may not use the Service.
								</p>

								<TitleText
									class="mt-[2.4rem]"
									type="6"
								>
									2. Eligibility
								</TitleText>

								<p class="mt-[1.6rem] text-[1.4rem] leading-[1.5]">
									To use our Service, you must be at least 18 years of age or the age of majority in your jurisdiction, and you must have the legal capacity to enter into a binding agreement. By using our Service, you represent and warrant that you meet these eligibility requirements.
								</p>

								<TitleText
									class="mt-[2.4rem]"
									type="6"
								>
									3. Account Registration
								</TitleText>

								<p class="mt-[1.6rem] text-[1.4rem] leading-[1.5]">
									To make a reservation, you may be required to create an account. When creating an account, you agree to:
								</p>

								<ul class="pl-[1.5rem] mt-[2.4rem] list-disc text-[1.4rem] leading-[1.5]">
									<li>Provide accurate, current, and complete information.</li>
									<li class="mt-[1rem]">
										Maintain and promptly update your account information.
									</li>
									<li class="mt-[1rem]">
										Keep your password secure and confidential.
									</li>
									<li class="mt-[1rem]">
										Notify us immediately of any unauthorized use of your account.
									</li>
								</ul>

								<p class="mt-[1.6rem] text-[1.4rem] leading-[1.5]">
									You are responsible for all activities that occur under your account. We are not liable for any loss or damage arising from your failure to comply with these requirements.
								</p>

								<TitleText
									class="mt-[2.4rem]"
									type="6"
								>
									4. Reservations
								</TitleText>

								<p class="mt-[1.6rem] text-[1.4rem] leading-[1.5]">
									When you make a reservation through our Service:
								</p>

								<ul class="pl-[1.5rem] mt-[2.4rem] list-disc text-[1.4rem] leading-[1.5]">
									<li>You agree to provide accurate reservation details.</li>
									<li class="mt-[1rem]">
										You acknowledge that the reservation is subject to availability and the restaurant's terms and conditions.
									</li>
									<li class="mt-[1rem]">
										You understand that cancellations or changes to reservations may be subject to the restaurant's policies, which may include fees.
									</li>
								</ul>

								<TitleText
									class="mt-[2.4rem]"
									type="6"
								>
									5. Payments
								</TitleText>

								<p class="mt-[1.6rem] text-[1.4rem] leading-[1.5]">
									If you make a payment through our Service:
								</p>

								<ul class="pl-[1.5rem] mt-[2.4rem] list-disc text-[1.4rem] leading-[1.5]">
									<li>You agree to provide accurate payment information.</li>
									<li class="mt-[1rem]">
										You authorize us to charge the payment method you provide for the total amount due.
									</li>
									<li class="mt-[1rem]">
										All payments are processed securely by our third-party payment processor. We do not store your payment information.
									</li>
								</ul>

								<TitleText
									class="mt-[2.4rem]"
									type="6"
								>
									6. Prohibited Conduct
								</TitleText>

								<p class="mt-[1.6rem] text-[1.4rem] leading-[1.5]">
									You agree not to:
								</p>

								<ul class="pl-[1.5rem] mt-[2.4rem] list-disc text-[1.4rem] leading-[1.5]">
									<li>Use the Service for any unlawful or unauthorized purpose.</li>
									<li class="mt-[1rem]">
										Interfere with or disrupt the security, integrity, or performance of the Service.
									</li>
									<li class="mt-[1rem]">
										Attempt to gain unauthorized access to the Service or its related systems.
									</li>
									<li class="mt-[1rem]">
										Engage in any activity that could harm or negatively impact other users or the Service.
									</li>
									<li class="mt-[1rem]">
										Use any automated means (e.g., bots, spiders) to access the Service.
									</li>
								</ul>

								<TitleText
									class="mt-[2.4rem]"
									type="6"
								>
									7. Intellectual Property
								</TitleText>

								<p class="mt-[1.6rem] text-[1.4rem] leading-[1.5]">
									All content on our Service, including text, graphics, logos, icons, images, and software, is the property of [Restaurant Reservation System Name] or its licensors and is protected by copyright, trademark, and other intellectual property laws. You may not use, reproduce, modify, or distribute any content without our express written permission.
								</p>

								<TitleText
									class="mt-[2.4rem]"
									type="6"
								>
									8. Disclaimer of Warranties
								</TitleText>

								<p class="mt-[1.6rem] text-[1.4rem] leading-[1.5]">
									The Service is provided on an "as is" and "as available" basis, without any warranties of any kind, whether express or implied. We do not warrant that the Service will be uninterrupted, error-free, or free from viruses or other harmful components.
								</p>

								<TitleText
									class="mt-[2.4rem]"
									type="6"
								>
									9. Limitation of Liability
								</TitleText>

								<p class="mt-[1.6rem] text-[1.4rem] leading-[1.5]">
									To the fullest extent permitted by law, [Restaurant Reservation System Name] shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses, resulting from:
								</p>

								<ul class="pl-[1.5rem] mt-[2.4rem] list-disc text-[1.4rem] leading-[1.5]">
									<li>Your use of or inability to use the Service.</li>
									<li class="mt-[1rem]">
										Any unauthorized access to or use of our servers and/or any personal information stored therein.
									</li>
									<li class="mt-[1rem]">
										Any errors, mistakes, or inaccuracies of content.
									</li>
									<li class="mt-[1rem]">
										Any bugs, viruses, trojan horses, or the like that may be transmitted to or through our Service by any third party.
									</li>
								</ul>

								<TitleText
									class="mt-[2.4rem]"
									type="6"
								>
									10. Indemnification
								</TitleText>

								<p class="mt-[1.6rem] text-[1.4rem] leading-[1.5]">
									You agree to indemnify, defend, and hold harmless [Restaurant Reservation System Name], its affiliates, and their respective officers, directors, employees, agents, and licensors from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorneys' fees, arising out of or in any way connected with:
								</p>

								<ul class="pl-[1.5rem] mt-[2.4rem] list-disc text-[1.4rem] leading-[1.5]">
									<li>Your access to or use of the Service.</li>
									<li class="mt-[1rem]">
										Your violation of these Terms.
									</li>
									<li class="mt-[1rem]">
										Your violation of any rights of another party, including intellectual property rights.
									</li>
								</ul>

								<TitleText
									class="mt-[2.4rem]"
									type="6"
								>
									11. Modifications to the Service
								</TitleText>

								<p class="mt-[1.6rem] text-[1.4rem] leading-[1.5]">
									We reserve the right to modify or discontinue the Service (or any part thereof) at any time, with or without notice. We are not liable to you or any third party for any modification, suspension, or discontinuation of the Service.
								</p>

								<TitleText
									class="mt-[2.4rem]"
									type="6"
								>
									12. Changes to These Terms
								</TitleText>

								<p class="mt-[1.6rem] text-[1.4rem] leading-[1.5]">
									We may update these Terms from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. Any changes will be posted on this page, and the "Last Updated" date will be revised accordingly. Your continued use of the Service after such changes constitutes your acceptance of the new Terms.
								</p>

								<TitleText
									class="mt-[2.4rem]"
									type="6"
								>
									13. Governing Law and Jurisdiction
								</TitleText>

								<p class="mt-[1.6rem] text-[1.4rem] leading-[1.5]">
									These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], without regard to its conflict of law principles. Any legal action or proceeding arising under these Terms will be brought exclusively in the courts located in [Your Jurisdiction], and you hereby consent to the personal jurisdiction and venue therein.
								</p>

								<TitleText
									class="mt-[2.4rem]"
									type="6"
								>
									14. Contact Us
								</TitleText>

								<p class="mt-[1.6rem] text-[1.4rem] leading-[1.5]">
                                    If you have any questions or concerns about this Privacy Policy or our data practices, please 
                                    <NuxtLink to="/contact" class="text-blue-600 underline">contact us here</NuxtLink>.
                                </p>

								<ul class="pl-[1.5rem] mt-[2.4rem] list-disc text-[1.4rem] leading-[1.5]">
									<li>Email: [info@kichieats.ph]</li>
									<li class="mt-[1rem]">
										Address: Level 21 Unit 2116-17, Park Triangle Corporate Plaza, North Tower 32nd Street Corner 11th Avenue Bonifacio Global City, Fort Bonifacio, Taguig City 1630
									</li>
								</ul>
							</Block>
						</Block>
					</Block>
				</Block>
			</Section>
		</Section>
	</Block>
</template>
